import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, TOKEN_KEY, USER_KEY, setOnUnauthorized } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(() => {
        try {
            const raw = localStorage.getItem(USER_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    });
    const [loading, setLoading] = useState(true);

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(null);
    }, []);

    useEffect(() => {
        setOnUnauthorized(logout);
    }, [logout]);

    useEffect(() => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) {
            setLoading(false);
            return;
        }
        api.get("/auth/me")
            .then((r) => {
                setUser(r.data);
                localStorage.setItem(USER_KEY, JSON.stringify(r.data));
            })
            .catch(() => {
                logout();
            })
            .finally(() => setLoading(false));
    }, [logout]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem(TOKEN_KEY, data.access_token);
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        setUser(data.user);
        return data.user;
    };

    const register = async (email, password, name) => {
        const { data } = await api.post("/auth/register", { email, password, name });
        localStorage.setItem(TOKEN_KEY, data.access_token);
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        setUser(data.user);
        return data.user;
    };

    const has = (...roles) => Boolean(user && roles.includes(user.role));

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, has }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
